import { LitElement, css, html } from 'lit';
import { property, customElement, query, queryAll } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);



@customElement('app-footer')
export class AppFooter extends LitElement {
  @property({ type: String }) title = 'Simple Tech Care';

  @property({ type: Boolean}) enableBack: boolean = false;
  @query('#footer') footer!: HTMLElement;
  @queryAll('p') paragraphs!: NodeListOf<HTMLParagraphElement>;
  @query('wa-button') contactButton!: HTMLElement;

  static styles = css`
    footer {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      background: var(--wa-color-blue);
      color: white;
      padding: 24px;
      text-align: center;
    }

    footer h2 {
      margin: 0;
      font-size: 18px;
      font-weight: bold;
    }

    footer p {
      margin: 0;
      font-size: 18px;
    }

    wa-button::part(base) {
      /* 44px is the recommended minimum touch target for touch and motor access. */
      min-height: 44px;
    }

    @media(prefers-color-scheme: light) {
      footer {
        color: black;
      }
    }
  `;

  private animationContext?: gsap.Context;
  private refreshFrame?: number;

  firstUpdated(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // gsap.context scopes animations to this shadow root so one revert() cleans up.
    this.animationContext = gsap.context(() => {
let tl = gsap.timeline({
  scrollTrigger: {
          trigger: this.footer,
          start: 'clamp(top 85%)',
          toggleActions: 'play none none none',
          // Reverse only when scrolling back up on a scrollable page; on a short
          // page the trigger sits "before start" and would otherwise hide the footer.
          onLeaveBack: (self) => {
            if (ScrollTrigger.maxScroll(window) >= 1) {
              self.animation?.reverse();
            }
          },
          // Refresh after pinned triggers so their pin spacers are measured first.
          refreshPriority: -2,
          // On a page too short to scroll, the clamped start is 0 and ScrollTrigger
          // never "enters", so reveal the footer directly.
          onRefresh: (self) => {
            if (ScrollTrigger.maxScroll(window) < 1) {
              self.animation?.progress(1);
            }
          },
        },
});

      tl.from(this.footer, {
        y: 48,
        autoAlpha: 0,
        duration: 0.75,
        ease: 'power3.out',

      })
      .from(this.paragraphs, {
        y: 24,
        autoAlpha: 0,
        duration: 1,
        ease: 'power3.out',
        stagger: 0.1,
      }, '-=0.5')
      .from(this.contactButton, {
       autoAlpha: 0,
          scale: 0.7,
          transformOrigin: 'center center',
          duration: 2.5,
          ease: 'elastic.out(1,0.3)',
      },'<');
    }, this.renderRoot);

    // The browser needs one frame to lay out before trigger positions are measured.
    this.refreshFrame = requestAnimationFrame(() => ScrollTrigger.refresh());
  }

  disconnectedCallback(): void {
    if (this.refreshFrame !== undefined) {
      cancelAnimationFrame(this.refreshFrame);
    }
    this.animationContext?.revert();
    super.disconnectedCallback();
  }

  render() {
    return html`
      <footer id="footer">


          <p>Ready to get started?</p>
          <p>Contact us today for a free consultation and quote.</p>
          <wa-button
            variant="brand"
            size="l"
            href="${resolveRouterPath('contact')}"
          >
            Contact Us
          </wa-button>

         <p>&copy; ${this.title} ${new Date().getFullYear()}</p>

      </footer>
    `;
  }
}
